import React from "react";
import { observer } from "mobx-react-lite";
import GroupByMenu from "./GroupByMenu";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import clsx from "clsx";
import DropdownBrowser from "components/DropdownBrowser";
import { PropertyBrowserTreeNode } from "api-client";
import { useTranslation } from "react-i18next";
import { CollectionIcon } from "@heroicons/react/outline";
import useOutsideClick from "hooks/useOutsideClick";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";

export default observer(function GroupByPropertyBrowser(props: {
  advancedMode?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const openIconRef = React.useRef<HTMLDivElement>(null);
  const filteredItems = dynamicTableStore.filterNotSelectedItems(
    dynamicTableStore.properties,
    dynamicTableStore.filterStore.selectedGroupByItems
  );
  const suggestedItems = dynamicTableStore.visibleColumnProperties.filter(
    (rec) =>
      !dynamicTableStore.filterStore.selectedGroupByItems.find(
        (item) => item.column_key === rec.column_key
      )
  );

  const closeView = () => dynamicTableStore.filterStore.setOpenGroupBy(false);
  useOutsideClick(openIconRef, closeView);

  const handleOpenMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    dynamicTableStore.filterStore.setOpenGroupBy(
      !dynamicTableStore.filterStore.openGroupBy
    );
  };

  const onSelectColumn = (selectedItems: PropertyBrowserTreeNode[]) => {
    dynamicTableStore.filterStore.setSelectedGroupByItems(selectedItems);
    dynamicTableStore.filterStore.reOrderGroupingColumns();
  };

  const clientRect = openIconRef.current?.getBoundingClientRect();

  function labelContent() {
    return (
      <div className="flex items-center">
        <CollectionIcon width={17} className="mr-1" strokeWidth={3} />
        <span className="mr-1">
          {dynamicTableStore.filterStore.validGroupByRules?.length
            ? `${t("mappingEditor.grouped")} ${t("mappingEditor.by")}: `
            : t("mappingEditor.group")}
        </span>
        {dynamicTableStore.filterStore.selectedGroupByItems
          .map((item) => (isEng ? item.name : item.name_de))
          .join(", ")}
      </div>
    );
  }

  const hasGrouping = Boolean(
    dynamicTableStore.filterStore.validGroupByRules?.length
  );

  return (
    <div
      className="flex items-center"
      ref={openIconRef}
      onClick={handleOpenMenu}
    >
      {hasGrouping && !props.advancedMode && labelContent()}
      <div className="overflow-visible">
        {hasGrouping ? (
          <>
            <IconChevronDown
              className={clsx(
                "h-5 w-5 cursor-pointer",
                dynamicTableStore.filterStore.openGroupBy ? "rotate-180" : ""
              )}
            />
            {dynamicTableStore.filterStore.openGroupBy ? (
              <GroupByMenu
                cursorPosition={{
                  x: Number(clientRect?.x),
                  y: Number(clientRect?.y),
                }}
                items={filteredItems}
                onSelectColumn={onSelectColumn}
                suggestedItems={suggestedItems}
              />
            ) : null}
          </>
        ) : (
          <DropdownBrowser<PropertyBrowserTreeNode>
            displayKey={isEng ? "name" : "name_de"}
            detailKey={isEng ? "classification" : "classification_de"}
            handleSelect={onSelectColumn}
            name="group-by"
            items={filteredItems}
            checkedItems={dynamicTableStore.filterStore.selectedGroupByItems}
            suggestedItems={suggestedItems}
            placeholder={t("mappingEditor.findProperty")}
            optionsClassName="!max-w-fit min-w-[246px]"
            containerClassName="relative"
            fixedPos
            titleOrIcon={
              <>
                {!props.advancedMode && labelContent()}
                <IconPlus
                  className={clsx("h-4 w-4 cursor-pointer", {
                    "ml-2": props.advancedMode,
                  })}
                />
              </>
            }
            isOpen={(open: boolean) =>
              dynamicTableStore.filterStore.setOpenGroupBy(open)
            }
          />
        )}
      </div>
    </div>
  );
});
